import { Mail, MapPin, Phone, Clock, Zap } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { LineReveal } from "@/components/LineReveal";
import { LeadForm } from "@/components/LeadForm";

const NEXT = [
  { title: "You send it", text: "Three fields. Tell us as much or as little as you like." },
  { title: "A human reads it", text: "Not a triage bot. The person who replies is the person who'll help." },
  { title: "You get an answer", text: "Within one working day, usually much faster, with a fixed-fee quote if work is needed." },
];

export default function Contact() {
  return (
    <main data-testid="contact-page">
      <Seo
        title="Contact Electra HR, Rapid HR Help for UK SMEs"
        siteName="Electra HR"
        description="Get in touch for fixed-fee HR support. Reply within one working day. Based in Rochester, Kent; working with founders across the UK."
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "LocalBusiness",
              "@id": `${window.location.origin}/#localbusiness`,
              name: "Electra HR",
              image: `${window.location.origin}/images/electra-hr-logo.png`,
              url: window.location.origin,
              telephone: "+44 1622 957300",
              email: "hello@electra-hr.com",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Suite 100, 7 Kenden Business Park, Maritime Close",
                addressLocality: "Rochester",
                addressRegion: "Kent",
                postalCode: "ME2 4JF",
                addressCountry: "GB",
              },
              openingHoursSpecification: [{
                "@type": "OpeningHoursSpecification",
                dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                opens: "09:00",
                closes: "17:30",
              }],
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
                { "@type": "ListItem", position: 2, name: "Contact", item: `${window.location.origin}/contact` },
              ],
            },
          ],
        }}
      />
      <section className="grain bg-ink pb-16 pt-32 sm:pt-40 text-paper">
        <div className="container-x">
          <p className="eyebrow text-pink">Contact</p>
          <LineReveal
            className="mt-6 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
            lines={["Say it plainly.", "We'll do the same."]}
            delay={0.1}
          />
        </div>
      </section>

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="border-2 border-ink bg-paper p-7 sm:p-10 shadow-[10px_10px_0_0_#F472B6]">
                <LeadForm source="contact" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <div className="clip-frame mb-8 overflow-hidden">
                <img src="/images/ELectra-lightning-800.webp" alt="The Electra HR lightning bolt" loading="lazy" className="h-40 w-full object-cover" />
              </div>
              <p className="eyebrow text-ink/50">Direct lines</p>
              <div className="mt-6 space-y-5">
                <a href="mailto:hello@electra-hr.com" data-testid="contact-email-link" className="flex items-center gap-4 border-2 border-ink/15 p-5 transition-colors hover:border-ink">
                  <Mail size={22} className="text-ink" /> <span className="font-medium">hello@electra-hr.com</span>
                </a>
                <a href="tel:+441622957300" data-testid="contact-phone-link" className="flex items-center gap-4 border-2 border-ink/15 p-5 transition-colors hover:border-ink">
                  <Phone size={22} className="text-ink" /> <span className="font-medium">01622 957 300</span>
                </a>
                <div className="flex items-center gap-4 border-2 border-ink/15 p-5">
                  <MapPin size={22} className="text-ink" /> <span className="font-medium">Suite 100, 7 Kenden Business Park, Maritime Close, Rochester, Kent ME2 4JF</span>
                </div>
                <div className="flex items-center gap-4 border-2 border-ink/15 p-5">
                  <Clock size={22} className="text-ink" /> <span className="font-medium">Replies within one working day</span>
                </div>
              </div>
              <div className="mt-10">
                <p className="eyebrow text-ink/50">What happens next</p>
                <ol className="mt-6 space-y-6">
                  {NEXT.map((s, i) => (
                    <li key={s.title} data-testid={`contact-next-step-${i}`} className="flex gap-5">
                      <span className="font-mono text-sm text-pink">0{i + 1}</span>
                      <div>
                        <p className="font-display font-semibold">{s.title}</p>
                        <p className="mt-1 text-sm leading-relaxed text-ink/60">{s.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="mt-10 flex gap-4 border-l-4 border-pink bg-ink p-6 text-paper">
                <Zap size={22} className="shrink-0 text-pink" />
                <p className="text-sm leading-relaxed text-paper/75">
                  Urgent, for example a dismissal decision, a grievance this morning or a walkout? Say "urgent" in your
                  message and we treat it as same-day.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
